// Export Registri HACCP (A4) — pagina dedicata (T-248).
// Spostato dall'hub inline su TodayPage a una pagina raggiungibile dal tile
// "Export registri" della dashboard. Genera i PDF A4 mensili firmati (firma
// eIDAS + hash) di tutti i registri MOD 01-11.

import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonPage,
  IonSelect, IonSelectOption, IonSpinner, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';
import { toUserMessage } from '../../lib/errors.js';
import { openExternalFromAsync, openExternalUrl } from '../../lib/openExternalUrl.js';

interface Registro { mod: string; label: string; fn: string | null; kind?: 'oil' | 'acrylamide' }

// Tutti i registri del manuale di base (MOD 01-11). fn = edge function di export.
export const REGISTRI: ReadonlyArray<Registro> = [
  { mod: '01', label: 'Tracciabilità materie prime (carico merci)', fn: 'export-carico-month' },
  { mod: '02', label: 'Tracciabilità cucina', fn: 'export-tracciabilita-month' },
  { mod: '03', label: 'Non conformità', fn: 'export-non-conformita-month' },
  { mod: '04', label: 'Controllo temperature frigoriferi', fn: 'export-temperature-month' },
  { mod: '05', label: 'Formazione', fn: 'export-formazione' },
  { mod: '06', label: 'Manutenzione attrezzature', fn: 'export-manutenzione-month' },
  { mod: '07', label: 'Taratura strumenti', fn: 'export-taratura' },
  { mod: '08', label: 'Prodotti abbattuti', fn: 'export-abbattimento-month' },
  { mod: '09', label: 'Sanificazione / pulizie', fn: 'export-cleaning-month' },
  { mod: '10', label: 'Temperature trasporto', fn: 'export-trasporto-month' },
  { mod: '11', label: 'Pest control (infestanti)', fn: 'export-pest-control-month' },
  { mod: 'OLIO', label: 'Controllo olio', fn: 'export-process-control-month', kind: 'oil' },
  { mod: 'ACR', label: 'Controllo acrilammide', fn: 'export-process-control-month', kind: 'acrylamide' },
  { mod: 'FOR', label: 'Elenco fornitori', fn: 'export-suppliers' },
];

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export function RegistriPage(): JSX.Element {
  const { profile } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [busyMod, setBusyMod] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // PDF già generati ma non apribili automaticamente (iOS PWA standalone):
  // business+mod → url. La sede nella chiave impedisce di riaprire il PDF
  // firmato della sede precedente dopo uno switch del locale attivo.
  // Mostrano un bottone "Apri PDF" che apre l'URL reale con un gesto fresco.
  const [readyUrls, setReadyUrls] = useState<Record<string, string>>({});

  // Cambiare periodo invalida gli URL pronti (firmati per mese/anno specifici).
  function changeMonth(m: number): void { setMonth(m); setReadyUrls({}); }
  function changeYear(y: number): void { setYear(y); setReadyUrls({}); }
  function readyUrlFor(mod: string): string | undefined {
    const businessId = profile?.active_business_id;
    return businessId ? readyUrls[`${businessId}:${mod}`] : undefined;
  }

  async function generate(reg: Registro): Promise<void> {
    if (!reg.fn) { setToast(`MOD. ${reg.mod} — ${reg.label}: export in arrivo`); return; }
    if (!profile?.active_tenant_id) { setToast('Nessun ristorante attivo'); return; }
    if (!profile.active_business_id) { setToast('Nessun locale attivo'); return; }
    const fn = reg.fn;
    const tenantId = profile.active_tenant_id;
    const businessId = profile.active_business_id;
    const readyKey = `${businessId}:${reg.mod}`;
    setBusyMod(reg.mod);
    try {
      const res = await openExternalFromAsync(async () => {
        const { data, error } = await getSupabase().functions.invoke<{ pdf_url?: string }>(
          fn, {
            body: {
              tenant_id: tenantId,
              ...(reg.kind ? { kind: reg.kind } : {}),
              business_id: businessId,
              year,
              month,
            },
          },
        );
        if (error) throw error;
        if (!data?.pdf_url) throw new Error('Nessun URL restituito');
        return data.pdf_url;
      });
      if (res.url) {
        setReadyUrls((m) => ({ ...m, [readyKey]: res.url as string }));
      }
      setToast(res.blocked
        ? `${reg.label}: pronto — tocca "Apri PDF"`
        : `Registro generato: ${reg.label}`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusyMod(null);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Export registri</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          Scegli mese e anno, poi genera il registro A4 stampabile (pronto per l'ispezione ASL),
          con firma elettronica e hash di integrità.
        </p>
        {!profile?.active_tenant_id && (
          <p style={{ color: '#dc2626', fontSize: 13 }}>Nessun ristorante attivo.</p>
        )}
        {profile?.active_tenant_id && !profile.active_business_id && (
          <p style={{ color: '#dc2626', fontSize: 13 }}>Nessun locale attivo.</p>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <IonSelect label="Mese" value={month} onIonChange={(e) => changeMonth(Number(e.detail.value))} style={{ flex: 1 }}>
            {MESI.map((m, i) => <IonSelectOption key={i} value={i + 1}>{m}</IonSelectOption>)}
          </IonSelect>
          <IonSelect label="Anno" value={year} onIonChange={(e) => changeYear(Number(e.detail.value))} style={{ flex: 1 }}>
            {[now.getFullYear() - 1, now.getFullYear()].map((yr) => <IonSelectOption key={yr} value={yr}>{yr}</IonSelectOption>)}
          </IonSelect>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {REGISTRI.map((reg) => (
            <div key={reg.mod} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              padding: '10px 12px', borderRadius: 10, border: '1px solid #e2e8f0', background: reg.fn ? '#fff' : '#f8fafc',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>MOD. {reg.mod} — {reg.label}</div>
                {!reg.fn && <div style={{ fontSize: 11, color: '#94a3b8' }}>Export in arrivo</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {readyUrlFor(reg.mod) && (
                  <IonButton size="small" fill="solid" color="success"
                    onClick={() => void openExternalUrl(readyUrlFor(reg.mod) as string)}
                    style={{ textTransform: 'none', '--border-radius': '8px' } as React.CSSProperties}>
                    Apri PDF
                  </IonButton>
                )}
                <IonButton size="small" fill={reg.fn ? 'solid' : 'outline'} disabled={busyMod !== null || !reg.fn}
                  onClick={() => void generate(reg)}
                  style={{ textTransform: 'none', '--border-radius': '8px' } as React.CSSProperties}>
                  {busyMod === reg.mod ? <IonSpinner name="dots" /> : (reg.fn ? 'Genera' : 'Presto')}
                </IonButton>
              </div>
            </div>
          ))}
        </div>

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2400} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
